// =============================================================================
// Permission Types
// =============================================================================

export interface CRMPermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface AnalyticsPermissions {
  read: boolean;
}

export interface ChatPermissions {
  read: boolean;
}

export interface AdminPermissions {
  read: boolean;
  manage_users: boolean;
  manage_settings: boolean;
}

export interface ProjectsPermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface HiringPermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface FormsPermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface PortalPermissions {
  read: boolean;
  manage: boolean;
  staff_access: boolean;  // Staff login with client switching
}

export interface TeamHubPermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface CustomerSuccessPermissions {
  read: boolean;
  manage: boolean;
}

export interface ProductionPermissions {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface AgencyIntelPermissions {
  read: boolean;
}

export interface LiquorChatPermissions {
  read: boolean;
  manage: boolean;
}

export interface AdsPermissions {
  read: boolean;
  manage: boolean;
}

export interface UserPermissions {
  crm: CRMPermissions;
  analytics: AnalyticsPermissions;
  chat: ChatPermissions;
  admin: AdminPermissions;
  projects: ProjectsPermissions;
  hiring: HiringPermissions;
  forms: FormsPermissions;
  portal: PortalPermissions;
  team_hub: TeamHubPermissions;
  customer_success: CustomerSuccessPermissions;
  production: ProductionPermissions;
  agency_intel?: AgencyIntelPermissions;
  liquorchat?: LiquorChatPermissions;
  ads?: AdsPermissions;
}

// Default permissions for new users - ALL permissions OFF by default
// Users must be explicitly granted access to each feature
export const DEFAULT_PERMISSIONS: UserPermissions = {
  crm: { read: false, create: false, update: false, delete: false },
  analytics: { read: false },
  chat: { read: false }, // No chat access by default - must be explicitly granted
  admin: { read: false, manage_users: false, manage_settings: false },
  projects: { read: false, create: false, update: false, delete: false },
  hiring: { read: false, create: false, update: false, delete: false },
  forms: { read: false, create: false, update: false, delete: false },
  portal: { read: false, manage: false, staff_access: false },
  team_hub: { read: false, create: false, update: false, delete: false },
  customer_success: { read: false, manage: false },
  production: { read: false, create: false, update: false, delete: false },
  agency_intel: { read: false }, // No intel access by default
  liquorchat: { read: false, manage: false },
  ads: { read: false, manage: false },
};

// Permission schema for UI building
export interface PermissionSchemaAction {
  [action: string]: string; // action_name: description
}

export interface PermissionSchemaFeature {
  label: string;
  description: string;
  actions: PermissionSchemaAction;
}

export interface PermissionSchema {
  [feature: string]: PermissionSchemaFeature;
}

// =============================================================================
// User Types
// =============================================================================

export type UserRole = 'owner' | 'admin' | 'staff' | 'readonly';
export type UserStatus = 'active' | 'inactive';
export type UserType = 'employee' | 'client';

export interface User {
  id: string;
  email: string;
  display_name?: string;
  name?: string; // Alias for display_name for backwards compatibility
  role: UserRole;
  status: UserStatus;
  user_type?: UserType; // 'employee' or 'client' - primary classification
  is_employee?: boolean; // Deprecated, use user_type instead
  permissions: UserPermissions;
  usage_limit_tokens_monthly?: number;
  monthly_tokens_used?: number;
  monthly_tokens_remaining?: number;
  created_at: string;
  updated_at: string;
  avatar_url?: string;
}

// For backwards compatibility with existing code
export interface LegacyUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'employee';
  status: 'active' | 'inactive';
  createdAt: Date;
  lastActive: Date;
}

// =============================================================================
// System Settings
// =============================================================================

export interface SystemSettings {
  companyName: string;
  welcomeMessage: string;
  maxSessionsPerUser: number;
  allowImageUploads: boolean;
  allowFileUploads: boolean;
  retentionDays: number;
  apiRateLimit: number;
}

// =============================================================================
// Analytics
// =============================================================================

export interface AnalyticsData {
  totalSessions: number;
  totalMessages: number;
  activeUsers: number;
  commandsUsed: number;
  avgSessionDuration: string;
  topCommands: { name: string; count: number }[];
  dailyActivity: { date: string; sessions: number; messages: number }[];
}

// =============================================================================
// User Update Request
// =============================================================================

export interface UserUpdateRequest {
  role?: UserRole;
  status?: UserStatus;
  user_type?: UserType;
  display_name?: string;
  permissions?: Partial<UserPermissions>;
  usage_limit_tokens_monthly?: number;
}
